/**
 * Per-app detailed stats page.
 *
 * Generic across apps: reuses MetricTiles, StreakDistributionBar,
 * ActivityHeatmap, and pulls per-CEFR-level rows for apps that have them
 * (nouns/derdiedas). The Simulations panel renders only when the app's
 * stats include a non-null `simulations` field (einbuergerungstest today).
 */
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { ActivityHeatmap } from '@/components/stats/ActivityHeatmap';
import { MetricTiles } from '@/components/stats/MetricTiles';
import { SimulationsSection } from '@/components/stats/SimulationsSection';
import { StreakDistributionBar } from '@/components/stats/StreakDistributionBar';
import { Button } from '@/components/ui/button';
import { useApps, useAppStats } from '@/lib/queries';

export function StatsRoute() {
  const { appId } = useParams<{ appId: string }>();
  const appsQuery = useApps();
  const statsQuery = useAppStats(appId);

  const app = appsQuery.data?.apps.find((a) => a.id === appId);

  return (
    <div className="min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/apps/${appId}`}>
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <h1 className="font-display text-xl font-semibold">
            {app?.display_name ?? appId} · Stats
          </h1>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-8 px-4 py-6 sm:px-6 sm:py-10">
        {statsQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : statsQuery.error ? (
          <p className="text-destructive text-sm">
            Couldn't load stats: {statsQuery.error.message}
          </p>
        ) : statsQuery.data ? (
          <>
            <section className="flex flex-col gap-4">
              <h2 className="font-display text-lg font-semibold">Overview</h2>
              <MetricTiles {...statsQuery.data.metrics} />
            </section>

            {hasAnyLearningCards(statsQuery.data.streak_distribution) ? (
              <section className="flex flex-col gap-3">
                <h2 className="font-display text-lg font-semibold">Streak distribution</h2>
                <p className="text-muted-foreground text-xs">
                  Where your in-progress questions sit on the 0–5 streak counter. At 5 the question
                  is marked Learned.
                </p>
                <StreakDistributionBar distribution={statsQuery.data.streak_distribution} />
              </section>
            ) : null}

            <section className="flex flex-col gap-3">
              <h2 className="font-display text-lg font-semibold">Activity</h2>
              <ActivityHeatmap data={statsQuery.data.activity_heatmap} />
            </section>

            {statsQuery.data.simulations ? (
              <SimulationsSection sims={statsQuery.data.simulations} showHistory />
            ) : null}
          </>
        ) : null}
      </main>
    </div>
  );
}

function hasAnyLearningCards(d: {
  s0: number;
  s1: number;
  s2: number;
  s3: number;
  s4plus: number;
}): boolean {
  return d.s0 + d.s1 + d.s2 + d.s3 + d.s4plus > 0;
}
